import mongoose from "mongoose";

export enum ComponentEnum {
  VIDEO = "Video",
  IMAGE = "Image",
  TEXT = "Text",
  INPUT = "Input",
  HABIT = "Habit",
  MULTIPLE_CHOICE = "Multiple Choice",
  RATING_SCALE = "Rating Scale",
  TOGGLE_TEXT = "Toggle Text",
  LOGIC_COMPONENT = "Logic Component",
  LESSON_LINK = "Lesson Link",
  USER_LIST = "User List",
  CUSTOM_LINK = "Custom Link",
  ACTION_DETAIL = "Action Detail",
  DATE_TIME = "Date Time",
  PRIORITY_LIST = "Priority List",
  CALLOUT_BOX = "Callout Box",
  FOR_EACH = "For Each",
  TOGGLE = "Toggle",
  ADD_TO_CALENDAR = "Add to Calendar",
}

export const inputComponents = new Set([
  ComponentEnum.INPUT,
  ComponentEnum.MULTIPLE_CHOICE,
  ComponentEnum.RATING_SCALE,
  ComponentEnum.USER_LIST,
  ComponentEnum.DATE_TIME,
  ComponentEnum.PRIORITY_LIST,
]);

export enum SignInStatus {
  SUCCESS = "SUCCESS",
  UNAUTHORIZED = "UNAUTHORIZED",
  NEW_PASSWORD_REQUIRED = "NEW_PASSWORD_REQUIRED",
  PENDING_CONFIRMATION = "PENDING_CONFIRMATION",
}

export interface SignInRes {
  status: SignInStatus;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  userSession?: any;
  // eslint-disable-next-line
  error?: any;
}

export enum CategoryEnum {
  CAREER = "Career",
  CLOSE_RELATIONSHIPS = "Close Relationships",
  GROWTH_AND_WELL_BEING = "Growth & Well-being",
  FAMILY_AND_PARENTING = "Family & Parenting",
}

export enum TopicStatus {
  Draft = "Draft",
  Published = "Published",
  Archived = "Archived",
  Coming_Soon = "Coming Soon",
}

// Backward-compatible alias during topic terminology transition.
export { TopicStatus as ModuleStatus };

export enum FeedbackValue {
  NOT_AT_ALL_USEFUL = "not_at_all_useful",
  MODERATELY_USEFUL = "moderately_useful",
  VERY_USEFUL = "very_useful",
}

export enum GeneralFeedbackTypeEnum {
  PAGE_FEEDBACK = "page_feedback",
  GENERAL_FEEDBACK = "general_feedback",
  BUG_REPORT = "bug_report",
  FEATURE_REQUEST = "feature_request",
  LESSON_FEEDBACK = "lesson_feedback",
}

export enum ResponseEnum {
  ACTIVITY = "Activity_New",
  REFLECTION = "Reflection",
  SECTION_RECAP = "Section Recap",
}

export enum ImageTypeEnum {
  ICON = "Icon",
  ILLUSTRATION = "Illustration",
  THUMBNAIL = "Thumbnail",
}

export enum BookmarkTypeEnum {
  MODULE = "module",
  PAGE = "page",
}

export interface ComponentI {
  _id?: string;
  type: ComponentEnum;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  content: any;
}

export interface PageI {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filter?: any;
  _id?: string;
  title: string;
  components: ComponentI[];
  keyTakeaways?: string;
  freezeVariablesFromPreviousPage?: string[];
  // Soft delete fields (7-day retention before permanent deletion)
  isDeleted?: boolean;
  deletionRequestedAt?: string | null;
  // Archive field - hidden from content manager but not deleted
  // If archivedAt is set, the page is archived
  archivedAt?: string | null;
  isToolboxTool?: boolean;
}

export interface ImageI {
  _id?: string;
  src: string;
  key: string;
  name: string;
  alt: string;
  type: ImageTypeEnum;
}

export enum BottomNavigationBarType {
  MULTI_PAGE = "Multi Page",
  NONE = "None",
  SINGLE_PAGE = "Single Page",
}

export interface LessonI {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filter?: any;
  _id?: string;
  title: string;
  slug?: string;
  icon: ImageI;
  pages: PageI[];
  bottomNavigationBarType?: BottomNavigationBarType;
}

export interface TopicI {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filter?: any;
  _id?: string;
  title: string;
  slug?: string;
  subtitle?: string;
  description: string;
  color: string;
  icon: ImageI;
  thumbnail?: ImageI | null;
  lessons: LessonI[];
  categories: CategoryEnum[];
  status: TopicStatus;
}

// Backward-compatible alias during topic terminology transition.
export type ModuleI = TopicI;

export interface RecapI {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  content?: any[];
  topic?: TopicI | string;
  module?: TopicI | string;
  lesson: LessonI | string;
  page?: PageI | string;
  completed?: boolean;
  pages_visited?: string[];
}

export interface ResponseI {
  _id?: mongoose.Types.ObjectId;
  topic?: TopicI | string;
  module?: TopicI | string;
  lesson?: LessonI | string;
  page?: PageI | string;
  title: string;
  keyTakeaway?: string;
  type: ResponseEnum;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  content: any;
  isCompletion?: boolean;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface BookmarkI {
  _id?: string;
  topic?: TopicI;
  module?: TopicI;
  page?: PageI;
  lesson?: LessonI;
  type: BookmarkTypeEnum;
  createdAt?: Date;
  updatedAt?: Date;
  lastActive?: Date;

  frozenComponents?: ComponentI[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  frozenAnswers?: Record<string, any>;
}

export enum UserRolesEnum {
  USER = "user",
  ADMIN = "admin",
}

export enum UserSegmentEnum {
  TEAM = "team",
  BETA_TESTERS = "betatesters",
  ADVISORY_BOARD = "advisoryboard",
  EVALUATOR = "evaluator",
  TEST_ACCOUNT = "testaccount",
  ASU_CALM = "asucalm",
  CBIZ = "cbiz",
  ASU = "asu",
  USER_GROUP_A = "usergroupa",
  USER_GROUP_B = "usergroupb",
  FRIENDS_AND_FAMILY = "friendsandfamily",
  NO_SEGMENT = "nosegment",
  NO_DATA = "nodata",
}

export enum AnalyticsCategoryEnum {
  Lesson = "Lesson",
  Module = "Module",
  Auth = "Auth",
  Toolbox = "Toolbox",
  Video = "Video",
  Page = "Page",
}

export interface AnalyticsEvent {
  eventName?: string;
  category: AnalyticsCategoryEnum;
  elementId?: string;
}

export interface FeedbackI {
  _id?: string;
  userSub: string;
  lessonId?: string | mongoose.Types.ObjectId;
  moduleId?: string | mongoose.Types.ObjectId;
  pageNumber?: number;
  pageUrl?: string;
  feedbackType?: GeneralFeedbackTypeEnum;
  feedback?: FeedbackValue;
  subFeedback?: string[];
  otherFeedback?: string;
  canContact?: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface UserPreferences {
  saveAnswersToLog: boolean;
  askEveryTime: boolean;
  dismissedTutorials: string[];
  emailConsentDate?: Date;
  emailAccountIssues?: boolean;
  emailFeedbackRequests?: boolean;
  emailNewsUpdates?: boolean;
  videoPlaybackSpeed?: number;
  autoplayVideos?: boolean;
  videoVolume?: number;
}

export interface ToolboxPlanI {
  _id: string;
  topicId: string;
  goal: string;
  cue: string;
  timeframe: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface ToolboxI {
  _id?: mongoose.Types.ObjectId;
  userSub: string;
  module: string;
  skillsReadAt?: Date;
  actionsReadAt?: Date;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface ToolboxQuestionI {
  _id?: mongoose.Types.ObjectId;
  questionId: string;
  variableName: string;
  type: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  content: any;
}

export interface ToolboxProfileDefinitionI {
  _id?: mongoose.Types.ObjectId;
  topicId: mongoose.Types.ObjectId | string;
  version: number;
  /** Admin list ordering; omitted documents sort by `version`. */
  sortOrder?: number;
  isActive: boolean;
  displayName?: string;
  questions: ToolboxQuestionI[];
  createdAt?: Date;
  updatedAt?: Date;
}

export interface ToolboxCheckinDefinitionI {
  _id?: mongoose.Types.ObjectId;
  topicId: mongoose.Types.ObjectId | string;
  version: number;
  isActive: boolean;
  displayName?: string;
  ratingEnabled: boolean;
  ratingScale?: number;
  ratingLabel?: string;
  questions: ToolboxQuestionI[];
  createdAt?: Date;
  updatedAt?: Date;
}

export interface UserToolboxProfileI {
  _id?: mongoose.Types.ObjectId;
  userSub: string;
  topicId: mongoose.Types.ObjectId | string;
  definitionId: mongoose.Types.ObjectId | string;
  /** Keys are each profile question's `variableName`. */
  answers: Record<string, unknown>;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface UserToolboxCheckinI {
  _id?: mongoose.Types.ObjectId;
  userSub: string;
  topicId: mongoose.Types.ObjectId | string;
  definitionId: mongoose.Types.ObjectId | string;
  rating?: number;
  ratingScale?: number;
  answers: Record<string, unknown>;
  createdAt?: Date;
  updatedAt?: Date;
}

// NEW: Legal and User Profile related interfaces
export interface LegalAcceptanceData {
  acceptLegal: boolean;
  version: string;
  agreedToEmail?: boolean;
}

export enum CognitoPoolEnum {
  STAGING = "staging",
  PRODUCTION = "production",
}

export interface UserProfileCreationData {
  userSub: string;
  username: string;
  email: string;
  segments: string[];
  roles: mongoose.Types.ObjectId[]; // Changed to ObjectId references
  cognitoPool?: CognitoPoolEnum;
  // Individual document acceptance tracking
  termsAcceptedDate?: Date;
  privacyAcceptedDate?: Date;
  eulaAcceptedDate?: Date;
  termsVersion?: string;
  privacyVersion?: string;
  eulaVersion?: string;
}

export interface SignUpRequestData {
  username: string;
  email: string;
  password: string;
  accessCode: string;
  legalAcceptance?: LegalAcceptanceData;
}

export interface UserProfileUpdateData {
  userSub: string;
  segments?: string[];
  roles?: UserRolesEnum[];
}

export interface AccessCodeValidationResult {
  success: boolean;
  message: string;
  data?: {
    accessCode: string;
    segment: string;
  };
}

export interface UserProfileUpdateFields {
  roles?: mongoose.Types.ObjectId[]; // Changed to ObjectId references
  segments?: UserSegmentEnum[];
  username?: string;
  displayName?: string;
  email?: string;
  lastLogin?: Date;
  cognitoPool?: CognitoPoolEnum;
  tutorialViewedDate?: Date;
  tutorialVersion?: string;
  tutorialVideoWatchedDate?: Date;
  // Individual document acceptance tracking
  termsAcceptedDate?: Date;
  privacyAcceptedDate?: Date;
  eulaAcceptedDate?: Date;
  termsVersion?: string;
  privacyVersion?: string;
  eulaVersion?: string;
  // Soft delete fields
  isDeleted?: boolean;
  deletionRequestedAt?: Date;
}

export interface ErrorReportI {
  _id?: string;
  userSub?: string;
  message: string;
  statusCode: number;
  statusText: string;
  url: string;
  timestamp: Date;
  userDescription?: string;
  pageUrl?: string;
  resolved?: boolean;
  createdAt?: Date;
  updatedAt?: Date;
}

export enum AnnouncementTypeEnum {
  INFO = "info",
  UPDATE = "update",
  FEATURE = "feature",
}

export enum AnnouncementPriorityEnum {
  LOW = "low",
  MEDIUM = "medium",
  HIGH = "high",
  CRITICAL = "critical",
}

export interface AnnouncementActionI {
  label: string;
  url?: string;
  variant?: "primary" | "secondary";
}

export interface AnnouncementI {
  _id: string;
  title: string;
  message: string;
  type: AnnouncementTypeEnum;
  priority: AnnouncementPriorityEnum;
  actions?: AnnouncementActionI[];
  expiresAt?: Date;
  startsAt?: Date;
  createdBy: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface UserAnnouncementInteractionI {
  _id: string;
  announcementId: string;
  userSub: string;
  viewedAt?: Date;
  dismissedAt?: Date;
  actionClicked?: string;
  actionClickedAt?: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface RoleI {
  _id?: mongoose.Types.ObjectId;
  name: string;
  displayName: string;
  description?: string;
  permissions: string[];
  createdAt?: Date;
  updatedAt?: Date;
}

export enum AuditActionType {
  ROLE_ASSIGNED = "ROLE_ASSIGNED",
  ROLE_REMOVED = "ROLE_REMOVED",
  MARKED_FOR_DELETION = "MARKED_FOR_DELETION",
  DELETION_CANCELLED = "DELETION_CANCELLED",
  ACCOUNT_DELETED = "ACCOUNT_DELETED",
  CSV_EXPORT = "CSV_EXPORT",
}

export interface UserRoleAuditLogI {
  _id?: string;
  changedBy: {
    userSub: string;
    userName: string;
    email: string;
  };
  targetUser: {
    userSub: string;
    userName: string;
    email: string;
  };
  role?: {
    roleId: string;
    roleName: string;
    roleDisplayName: string;
  };
  actionType: AuditActionType;
  rolesChanged?: {
    before: string[];
    after: string[];
    beforeNames: string[];
    afterNames: string[];
  };
  metadata?: {
    filters?: Record<string, string>;
    fields?: string[];
    exportedCount?: number;
  };
  timestamp: Date;
  createdAt?: Date;
  updatedAt?: Date;
}
